import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Mail, Search, Download, ChevronUp, ChevronDown, Pencil, Save, X, RotateCcw } from "lucide-react";
import Flag from "../../components/Flag";

const fmt = (s) => `${(s || 0).toFixed(1)}s`;

export default function AdminCredentials() {
  const { quizId } = useParams();
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const [bulkEmail, setBulkEmail] = useState("");
  const [bulkCount, setBulkCount] = useState(10);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("username");
  const [sortDir, setSortDir] = useState("asc");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editSchool, setEditSchool] = useState("");

  const load = async () => {
    const { data } = await api.get(`/admin/quizzes/${quizId}/credentials`);
    setItems(data || []);
  };
  useEffect(() => { load(); }, [quizId]);

  const createOne = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/credentials", { quiz_id: quizId, email });
      setEmail("");
      toast.success("Credencial gerada");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const bulk = async () => {
    if (!bulkEmail.trim() || !bulkEmail.includes("@")) { toast.error("O email do professor é obrigatório"); return; }
    try {
      await api.post("/admin/credentials/bulk", null, { params: { quiz_id: quizId, count: bulkCount, email: bulkEmail.trim() } });
      toast.success(`${bulkCount} credenciais geradas`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const remove = async (id) => {
    await api.delete(`/admin/credentials/${id}`);
    load();
  };

  const startEdit = (c) => {
    if (c.result_name == null && c.result_school == null) { toast.error("Este aluno ainda não participou"); return; }
    setEditId(c.id); setEditName(c.result_name || ""); setEditSchool(c.result_school || "");
  };
  const cancelEdit = () => { setEditId(null); };
  const saveEdit = async (c) => {
    try {
      await api.patch(`/admin/credentials/${c.id}/participant`, { name: editName, school: editSchool });
      toast.success("Nome/Escola atualizados");
      setEditId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const sendEmail = async (c) => {
    const target = window.prompt("Email destinatário:", c.email || "");
    if (!target) return;
    const lang = window.confirm("OK = enviar versão PT, Cancelar = enviar versão EN") ? "pt" : "en";
    try {
      const { data } = await api.post("/admin/send-credential", {
        credential_id: c.id, email: target, lang,
      });
      if (data.ok) toast.success("Email enviado");
      else toast.error("Falha no envio");
      load();
    } catch (e) { toast.error("Erro"); }
  };

  const sendBulk = async () => {
    const lang = window.confirm("OK = enviar versão PT a todos, Cancelar = enviar versão EN a todos") ? "pt" : "en";
    if (!window.confirm(`Confirmar envio em lote (${lang.toUpperCase()}) para TODAS as credenciais com email preenchido?`)) return;
    toast.message("A enviar... pode demorar alguns segundos");
    try {
      const { data } = await api.post(`/admin/quizzes/${quizId}/send-credentials-bulk`, null, { params: { lang } });
      toast.success(`Enviados: ${data.sent} • Falharam: ${data.failed} • Ignorados (sem email): ${data.skipped_no_email}`);
    } catch (e) { toast.error("Erro no envio em lote"); }
  };

  const removeAll = async () => {
    if (!window.confirm("Eliminar TODAS as credenciais deste quiz?")) return;
    await api.delete(`/admin/quizzes/${quizId}/credentials`);
    load();
  };

  const resetAttempt = async (c) => {
    const who = c.result_name ? `"${c.result_name}" (${c.username})` : `credencial ${c.username}`;
    if (!window.confirm(
      `Reset de ${who}?\n\nIsto apaga o registo de participação atual e liberta a credencial para o aluno voltar a entrar e tentar outra vez.`
    )) return;
    try {
      const { data } = await api.post(`/admin/credentials/${c.id}/reset`);
      toast.success(data.cleared_participations > 0
        ? `Credencial reposta — ${data.cleared_participations} registo(s) de participação apagado(s).`
        : "Credencial reposta.");
      setEditId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao fazer reset"); }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const q = search.trim().toLowerCase();
  const displayed = items
    .filter((c) => {
      if (!q) return true;
      return [c.username, c.email, c.result_name, c.result_school, c.result_quiz].some((v) => (v || "").toString().toLowerCase().includes(q));
    })
    .sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      let av = a[sortKey], bv = b[sortKey];
      if (["result_time", "result_wrong", "result_correct", "result_ranking"].includes(sortKey)) {
        av = av == null ? Infinity : Number(av);
        bv = bv == null ? Infinity : Number(bv);
        return (av - bv) * dir;
      }
      return (av || "").toString().localeCompare((bv || "").toString(), "pt") * dir;
    });

  const exportExcel = async () => {
    try {
      const resp = await api.get(`/admin/quizzes/${quizId}/credentials/export`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `participantes-${quizId}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erro ao exportar"); }
  };

  const SortTh = ({ label, k, align = "left" }) => (
    <th className={`text-${align} px-4 py-3 font-semibold cursor-pointer select-none`} onClick={() => toggleSort(k)} data-testid={`cred-sort-${k}`}>
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        {label}
        {sortKey === k && (sortDir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
      </span>
    </th>
  );

  return (
    <div data-testid="admin-credentials-page">
      <Link to="/admin/quizzes" className="inline-flex items-center gap-1 text-sm text-[#0B1D3A]/60 mb-4"><ArrowLeft size={14}/> Voltar</Link>
      <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A] mb-2">Credenciais</h1>
      <p className="text-sm text-[#0B1D3A]/55 mb-6">Os nomes de utilizador têm 8 dígitos numéricos e as palavras-passe 4 dígitos numéricos.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <form onSubmit={createOne} className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-sm font-semibold mb-1">Email do professor (obrigatório)</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="professor@escola.pt" className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="cred-email-input" />
          </div>
          <button className="bg-[#0B1D3A] text-white rounded-lg font-bold inline-flex items-center gap-2 px-4 py-2" data-testid="cred-generate-btn">
            <Plus size={16}/> Gerar
          </button>
        </form>
        <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5">
          <label className="block text-sm font-semibold mb-1">Geração em lote</label>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <input value={bulkEmail} onChange={(e) => setBulkEmail(e.target.value)} type="email" required placeholder="Email do professor (obrigatório)" className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15 mb-2" data-testid="cred-bulk-email" />
              <input type="number" min="1" max="500" value={bulkCount} onChange={(e) => setBulkCount(parseInt(e.target.value) || 1)} className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="cred-bulk-count" />
            </div>
            <button onClick={bulk} className="bg-[#FF6B6B] text-white rounded-lg font-bold px-4 py-2 whitespace-nowrap" data-testid="cred-bulk-btn">Gerar {bulkCount}</button>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0B1D3A]/40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar por utilizador, nome, escola ou email..." className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="cred-search-input" />
        </div>
        <div className="flex gap-3 items-center">
          <p className="text-sm text-[#0B1D3A]/60">{displayed.length} de {items.length}</p>
          {items.length > 0 && (
            <button onClick={exportExcel} className="text-xs px-3 py-1.5 rounded-md bg-[#2D9C5A] text-white hover:bg-[#258a4f] inline-flex items-center gap-1.5 font-semibold" data-testid="cred-export-btn">
              <Download size={14}/> Exportar Excel
            </button>
          )}
          {items.length > 0 && (
            <button onClick={sendBulk} className="text-xs px-3 py-1.5 rounded-md bg-[#005F73] text-white hover:bg-[#003847] inline-flex items-center gap-1.5 font-semibold" data-testid="cred-bulk-send-btn">
              <Mail size={14}/> Enviar credenciais em lote
            </button>
          )}
          {items.length > 0 && <button onClick={removeAll} className="text-xs text-red-600 hover:underline">Eliminar todas</button>}
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#0B1D3A]/5 overflow-x-auto">
        <table className="w-full compact-table">
          <thead className="bg-[#F8FAFC] text-[#0B1D3A]/70">
            <tr>
              <SortTh label="Username" k="username" />
              <th className="text-left px-4 py-3 font-semibold">Password</th>
              <SortTh label="Email" k="email" />
              <SortTh label="Nome" k="result_name" />
              <SortTh label="Escola" k="result_school" />
              <th className="text-left px-4 py-3 font-semibold">Nacionalidade</th>
              <SortTh label="Quiz" k="result_quiz" />
              <SortTh label="Tempo" k="result_time" align="right" />
              <SortTh label="Certas" k="result_correct" align="right" />
              <SortTh label="Erradas" k="result_wrong" align="right" />
              <SortTh label="Ranking" k="result_ranking" align="right" />
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {displayed.map((c) => (
              <tr key={c.id} className="border-t border-[#0B1D3A]/5" data-testid={`cred-row-${c.id}`}>
                <td className="px-4 py-3 font-mono">{c.username}</td>
                <td className="px-4 py-3 font-mono">{c.password}</td>
                <td className="px-4 py-3 text-[#0B1D3A]/70">{c.email || "—"}</td>
                <td className="px-4 py-3">
                  {editId === c.id
                    ? <input value={editName} onChange={(e) => setEditName(e.target.value)} className="w-32 px-2 py-1 rounded border border-[#005F73]/40" data-testid={`cred-edit-name-${c.id}`} />
                    : (c.result_name || "—")}
                </td>
                <td className="px-4 py-3">
                  {editId === c.id
                    ? <input value={editSchool} onChange={(e) => setEditSchool(e.target.value)} className="w-32 px-2 py-1 rounded border border-[#005F73]/40" data-testid={`cred-edit-school-${c.id}`} />
                    : (c.result_school || "—")}
                </td>
                <td className="px-4 py-3 text-[#0B1D3A]/70">{c.result_nationality ? <Flag code={c.result_nationality} showName /> : "—"}</td>
                <td className="px-4 py-3 text-[#0B1D3A]/70">{c.result_quiz || "—"}</td>
                <td className="px-4 py-3 text-right font-semibold text-[#005F73]">{c.result_abandoned ? <span className="text-[#E63946]">Desistência</span> : (c.result_timed_out ? <span className="text-[#9CA3AF]">Tempo esgotado</span> : (c.result_time != null ? fmt(c.result_time) : "—"))}</td>
                <td className="px-4 py-3 text-right text-[#2D9C5A] font-semibold">{!c.result_abandoned && c.result_time != null ? (c.result_correct ?? 0) : "—"}</td>
                <td className="px-4 py-3 text-right text-[#E63946] font-semibold">{!c.result_abandoned && c.result_time != null ? (c.result_wrong ?? 0) : "—"}</td>
                <td className="px-4 py-3 text-right font-bold text-[#0B1D3A]">{c.result_ranking != null ? `${c.result_ranking}º` : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2 items-center">
                    {editId === c.id ? (
                      <>
                        <button onClick={() => saveEdit(c)} className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md bg-[#2D9C5A] text-white" title="Guardar" data-testid={`cred-save-${c.id}`}><Save size={14}/></button>
                        <button onClick={cancelEdit} className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md bg-[#0B1D3A]/5 text-[#0B1D3A]" title="Cancelar" data-testid={`cred-cancel-${c.id}`}><X size={14}/></button>
                        <button onClick={() => resetAttempt(c)} className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md bg-[#FF9F1C]/15 text-[#B26A00] hover:bg-[#FF9F1C]/25" title="Fazer reset da conta (apaga participação e liberta a credencial)" data-testid={`cred-reset-${c.id}`}><RotateCcw size={14}/> Reset</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(c)} className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md bg-[#0B1D3A]/5 text-[#0B1D3A] hover:bg-[#0B1D3A]/10" title="Editar nome/escola" data-testid={`cred-edit-${c.id}`}><Pencil size={14}/></button>
                        {(c.used || c.result_name) && (
                          <button onClick={() => resetAttempt(c)} className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md bg-[#FF9F1C]/10 text-[#B26A00] hover:bg-[#FF9F1C]/20" title="Fazer reset da conta (apaga participação e liberta a credencial)" data-testid={`cred-reset-${c.id}`}><RotateCcw size={14}/></button>
                        )}
                        <button onClick={() => sendEmail(c)} className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-md bg-[#005F73]/10 text-[#005F73] hover:bg-[#005F73]/20" title="Enviar por email"><Mail size={14}/></button>
                        <button onClick={() => remove(c.id)} className="text-red-600"><Trash2 size={14}/></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {displayed.length === 0 && <tr><td colSpan="12" className="px-4 py-6 text-center text-[#0B1D3A]/50">Sem credenciais.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
